import * as React from 'react';
import {useLocation} from 'react-router-dom';

export type BreadcrumbItem = {
  label: string;
  href: string;
};

/**
 * One entry in the navigation trail. `key` is the stable identity used to
 * detect revisits (usually the pathname), while `href` is the full location
 * (pathname + search) captured when the page was visited, so navigating back
 * through the trail restores filters and tabs.
 */
export type TrailEntry = {
  key: string;
  label: string;
  href: string;
  /**
   * Account logo key (e.g. "chase"), rendered as `/logos/{logo}.png` next to
   * the label. Null/undefined for crumbs with no associated logo.
   */
  logo?: string | null;
};

export type PageCrumb = {
  label: string;
  /**
   * Root pages (side-nav destinations) reset the trail to just themselves.
   */
  reset?: boolean;
  /**
   * Identity used to match revisits. Defaults to the current pathname. Pages
   * with sub-routes that should share one crumb (e.g. account tabs) pass a
   * common key so switching tabs updates the entry instead of appending.
   */
  key?: string;
  /**
   * Canonical parent chain used to seed the trail when the page is reached
   * directly (deep link or refresh) with no journey to show.
   */
  fallback?: BreadcrumbItem[];
  /** See TrailEntry.logo. */
  logo?: string | null;
};

type BreadcrumbContextValue = {
  trail: TrailEntry[];
  register: (
    crumb: PageCrumb,
    location: {pathname: string; search: string},
  ) => void;
};

const MAX_TRAIL_LENGTH = 8;
const STORAGE_KEY = 'breadcrumb-trail';

const isTrailEntry = (value: unknown): value is TrailEntry =>
  typeof value === 'object' &&
  value !== null &&
  'key' in value &&
  typeof value.key === 'string' &&
  'label' in value &&
  typeof value.label === 'string' &&
  'href' in value &&
  typeof value.href === 'string';

/**
 * Remove the persisted trail. Called on logout so the next user in the same
 * tab doesn't inherit the previous user's navigation history (entity names
 * and URLs).
 */
export const clearStoredTrail = () => {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Best-effort; sessionStorage may be unavailable.
  }
};

const loadStoredTrail = (): TrailEntry[] => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(isTrailEntry).slice(-MAX_TRAIL_LENGTH);
  } catch {
    return [];
  }
};

const entriesEqual = (a: TrailEntry, b: TrailEntry) =>
  a.key === b.key &&
  a.label === b.label &&
  a.href === b.href &&
  a.logo === b.logo;

const trailsEqual = (a: TrailEntry[], b: TrailEntry[]) =>
  a.length === b.length && a.every((entry, i) => entriesEqual(entry, b[i]));

const BreadcrumbContext = React.createContext<BreadcrumbContextValue>({
  trail: [],
  register: () => {},
});

export const BreadcrumbProvider: React.FC<{children: React.ReactNode}> = ({
  children,
}) => {
  const [trail, setTrail] = React.useState<TrailEntry[]>(loadStoredTrail);
  // True until the first page registers after a full page load. The stored
  // trail is only trustworthy for that first registration when it already
  // contains the current page (refresh, or back/forward into the journey);
  // a fresh deep link to an unvisited page should seed from its fallback
  // rather than extend an unrelated stored trail.
  const isFirstRegistrationRef = React.useRef(true);

  React.useEffect(() => {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(trail));
    } catch {
      // Persistence is best-effort; the trail still works in memory.
    }
  }, [trail]);

  const register = React.useCallback(
    (crumb: PageCrumb, location: {pathname: string; search: string}) => {
      const key = crumb.key ?? location.pathname;
      const entry: TrailEntry = {
        key,
        label: crumb.label,
        href: `${location.pathname}${location.search}`,
        logo: crumb.logo ?? null,
      };
      const isFirstRegistration = isFirstRegistrationRef.current;
      isFirstRegistrationRef.current = false;

      setTrail((prev) => {
        let next: TrailEntry[];
        if (crumb.reset) {
          next = [entry];
        } else {
          const index = prev.findIndex((e) => e.key === key);
          if (index >= 0) {
            // Revisiting a page already in the trail (breadcrumb click,
            // browser back, or a navigation loop): rewind to it.
            next = [...prev.slice(0, index), entry];
          } else {
            // A hydrated trail that doesn't contain the page we booted on is
            // unrelated history (fresh deep link) — start from the fallback.
            const usePrev = prev.length > 0 && !isFirstRegistration;
            const base = usePrev
              ? prev
              : (crumb.fallback ?? []).map((item) => ({
                  key: item.href,
                  label: item.label,
                  href: item.href,
                }));
            next = [...base, entry].slice(-MAX_TRAIL_LENGTH);
          }
        }
        return trailsEqual(prev, next) ? prev : next;
      });
    },
    [],
  );

  const value = React.useMemo(() => ({trail, register}), [trail, register]);

  return (
    <BreadcrumbContext.Provider value={value}>
      {children}
    </BreadcrumbContext.Provider>
  );
};

/**
 * Declare the crumb for the current page. The provider assembles the visible
 * trail from the pages actually visited. Pass null to opt out (e.g. when the
 * component is embedded inside another page that owns the crumb).
 */
export const useBreadcrumb = (crumb: PageCrumb | null) => {
  const {register} = React.useContext(BreadcrumbContext);
  const location = useLocation();
  const crumbRef = React.useRef(crumb);
  crumbRef.current = crumb;
  const serialized = crumb === null ? null : JSON.stringify(crumb);

  React.useEffect(() => {
    const current = crumbRef.current;
    if (current === null) {
      return;
    }
    register(current, {
      pathname: location.pathname,
      search: location.search,
    });
  }, [serialized, register, location.pathname, location.search]);
};

export const useBreadcrumbContext = () => {
  return React.useContext(BreadcrumbContext);
};
